/**
 * Section — contenedor colapsable para comprimir vistas densas.
 *
 * Permite agrupar el contenido del dashboard en bloques plegables,
 * reduciendo la saturación visual.  El estado abierto/cerrado se
 * persiste en localStorage por `id` para que la preferencia del
 * operador sobreviva recargas.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";

import { Icon, type IconName } from "./Icon";

interface SectionProps {
  id: string;
  title: string;
  icon?: IconName;
  /** Texto de resumen mostrado a la derecha cuando está colapsada. */
  summary?: ReactNode;
  /** Acciones (botones) en el encabezado. */
  actions?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

const STORAGE_PREFIX = "anc.section.";

function readOpen(id: string, fallback: boolean): boolean {
  try {
    const v = window.localStorage.getItem(STORAGE_PREFIX + id);
    if (v === "open") return true;
    if (v === "closed") return false;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function Section({
  id,
  title,
  icon,
  summary,
  actions,
  defaultOpen = true,
  children,
}: SectionProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(() => readOpen(id, defaultOpen));

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + id, open ? "open" : "closed");
    } catch {
      /* ignore */
    }
  }, [id, open]);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  return (
    <section className="section" data-open={open}>
      <header className="section__header">
        <button
          type="button"
          className="section__toggle"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={`section-body-${id}`}
        >
          <span className={`section__chevron ${open ? "is-open" : ""}`}>
            <Icon name="chevron-right" size={14} />
          </span>
          {icon && (
            <span className="section__icon">
              <Icon name={icon} size={14} />
            </span>
          )}
          <span className="section__title">{title}</span>
        </button>
        <div className="section__meta">
          {!open && summary && <span className="section__summary">{summary}</span>}
          {actions && <div className="section__actions">{actions}</div>}
        </div>
      </header>
      {open && (
        <div className="section__body" id={`section-body-${id}`}>
          {children}
        </div>
      )}
    </section>
  );
}
